/**
 * AdminIonFeedbackSubtab — review queue for in-chat Ion feedback.
 *
 * Lives inside AdminAiManagementTab's sub-tab nav. Lets the three admins
 * filter feedback (default: their own open stack), reassign / resolve items,
 * and export each one as an "Export for Claude Code" Markdown bundle (copy or
 * download) to paste into a Claude Code session.
 *
 * Mirrors AdminConversionFeedbackTab (list + filter + detail) and reuses the
 * admin-aim-* styles for visual consistency.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { currentUser } from '../../state/userState.js';
import {
  getIonFeedback,
  updateIonFeedback,
  fetchIonFeedbackBundle,
  fetchIonFeedbackScreenshot,
  getFeedbackOwners,
  setFeedbackOwners,
} from '../../services/adminService.js';

const MIME_EXT = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };

const CATEGORY_LABELS = {
  'wrong-data': 'Wrong data',
  hallucination: 'Hallucination',
  'missing-feature': 'Missing feature',
  'bad-formatting': 'Bad formatting',
  ux: 'UX / design',
  other: 'Other',
  // Positive-polarity reasons (polarity='positive').
  accurate: 'Accurate',
  'well-explained': 'Well explained',
  'good-format': 'Good formatting',
  'great-workflow': 'Great workflow',
  'other-good': 'Other (good)',
};

const POLARITY_LABELS = { problem: 'Problem', positive: 'Good' };

const AREA_LABELS = {
  'frontend-ux': 'Frontend / UX',
  'backend-data': 'Backend / Data',
  'content-prompt': 'Content / Prompt',
  unknown: 'Triage',
};

const SEVERITY_LABELS = { low: 'Low', med: 'Medium', high: 'High' };

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function severityClass(sev) {
  if (sev === 'high') return 'admin-badge--error';
  if (sev === 'med') return 'admin-badge--warning';
  return 'admin-badge--basic';
}

function FeedbackRoutingBar() {
  const [candidates, setCandidates] = useState([]);
  const [frontendId, setFrontendId] = useState('');
  const [backendId, setBackendId] = useState('');
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState(null);

  const load = async () => {
    try {
      const data = await getFeedbackOwners();
      setCandidates(data.candidates || []);
      setFrontendId(data.frontend?.userId ? String(data.frontend.userId) : '');
      setBackendId(data.backend?.userId ? String(data.backend.userId) : '');
    } catch {
      setNote('Could not load routing config.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setNote(null);
    try {
      await setFeedbackOwners({
        frontendUserId: frontendId ? Number(frontendId) : null,
        backendUserId: backendId ? Number(backendId) : null,
      });
      setNote('Routing saved.');
      setTimeout(() => setNote(null), 2500);
    } catch (e) {
      setNote(`Save failed: ${e?.message || 'error'}`);
    } finally {
      setSaving(false);
    }
  };

  const ownerSelect = (value, setter, label) => (
    <label className="admin-aim-field" style={{ minWidth: 220 }}>
      <span className="ion-fb-field__label" style={{ fontSize: 12 }}>{label}</span>
      <select value={value} onChange={(e) => setter(e.target.value)}>
        <option value="">— Unassigned —</option>
        {candidates.map((c) => (
          <option key={c.userId} value={String(c.userId)}>
            {c.email}{c.name ? ` (${c.name})` : ''}
          </option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="admin-aim-field-grid" style={{ alignItems: 'flex-end', gap: 12, margin: '0 0 14px', flexWrap: 'wrap' }}>
      {ownerSelect(frontendId, setFrontendId, 'Frontend / UX owner')}
      {ownerSelect(backendId, setBackendId, 'Backend / Data owner')}
      <button type="button" className="admin-ai-btn admin-ai-btn--send" onClick={save} disabled={saving}>
        {saving ? 'Saving…' : 'Save routing'}
      </button>
      {note && <span className="admin-aim-meta">{note}</span>}
    </div>
  );
}

export function AdminIonFeedbackSubtab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [busyId, setBusyId] = useState(null);
  const [toast, setToast] = useState(null);
  const [shotUrls, setShotUrls] = useState({}); // feedbackId -> object URL
  // Default view: all open feedback. "Assigned to me" / "Unassigned" are
  // opt-in — defaulting to "mine" hid unassigned (triage) items and made the
  // list look empty before routing owners were configured.
  const [filters, setFilters] = useState({
    status: 'open',
    area: '',
    assigned_to: '',
    category: '',
    severity: '',
    polarity: '',
  });

  // /users/me exposes public_id, not the numeric id — so we can't compare by
  // id. Use email (which we do have) to tell "is this assigned to me", and let
  // the server resolve the caller's id for "Assign to me" (assignToMe flag).
  const myEmail = currentUser.value?.email;

  const params = useMemo(() => {
    const p = { limit: 100 };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) p[k] = v;
    });
    return p;
  }, [filters]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getIonFeedback(params);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const flashToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 2500);
  };

  const loadScreenshot = async (id) => {
    if (shotUrls[id]) return;
    try {
      const blob = await fetchIonFeedbackScreenshot(id);
      if (blob) {
        setShotUrls((prev) => ({ ...prev, [id]: URL.createObjectURL(blob) }));
      }
    } catch {
      /* non-fatal — thumbnail just won't show */
    }
  };

  const toggleExpand = (id, item) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (item?.hasScreenshot) loadScreenshot(id);
      }
      return next;
    });
  };

  const patch = async (id, body) => {
    setBusyId(id);
    try {
      await updateIonFeedback(id, body);
      await load();
    } catch (e) {
      flashToast(`Update failed: ${e?.message || 'error'}`, true);
    } finally {
      setBusyId(null);
    }
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportBundle = async (it, mode) => {
    setBusyId(it.id);
    try {
      const md = await fetchIonFeedbackBundle(it.id);
      if (mode === 'copy') {
        await navigator.clipboard.writeText(md);
        flashToast('Bundle copied to clipboard');
      } else {
        triggerDownload(new Blob([md], { type: 'text/markdown' }), `ion-feedback-${it.id}.md`);
        // Download the screenshot next to the .md so the bundle's
        // ![screenshot](ion-feedback-N.ext) reference resolves locally.
        if (it.hasScreenshot) {
          const blob = await fetchIonFeedbackScreenshot(it.id);
          if (blob) {
            const ext = MIME_EXT[blob.type] || 'png';
            triggerDownload(blob, `ion-feedback-${it.id}.${ext}`);
          }
        }
        flashToast('Bundle downloaded');
      }
    } catch (e) {
      flashToast(`Export failed: ${e?.message || 'error'}`, true);
    } finally {
      setBusyId(null);
    }
  };

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Ion Feedback</h2>
          <p className="admin-aim-sub">
            In-chat feedback from admins. Export any item as a Markdown bundle
            ("Export for Claude Code") to fix it in a Claude Code session.
          </p>
        </div>
      </header>

      <FeedbackRoutingBar />

      <div className="admin-aim-toolbar">
        <select value={filters.assigned_to} onChange={(e) => setFilter('assigned_to', e.target.value)}>
          <option value="">Anyone</option>
          <option value="mine">Assigned to me</option>
          <option value="unassigned">Unassigned (triage)</option>
        </select>
        <select value={filters.status} onChange={(e) => setFilter('status', e.target.value)}>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="">Any status</option>
        </select>
        <select value={filters.polarity} onChange={(e) => setFilter('polarity', e.target.value)}>
          <option value="">Problem + Good</option>
          <option value="problem">Problem only</option>
          <option value="positive">Good only</option>
        </select>
        <select value={filters.area} onChange={(e) => setFilter('area', e.target.value)}>
          <option value="">All areas</option>
          {Object.entries(AREA_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select value={filters.category} onChange={(e) => setFilter('category', e.target.value)}>
          <option value="">All categories</option>
          {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select value={filters.severity} onChange={(e) => setFilter('severity', e.target.value)}>
          <option value="">All severities</option>
          {Object.entries(SEVERITY_LABELS).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={load}>↻</button>
        <span className="admin-aim-meta">{total} total</span>
      </div>

      {toast && (
        <div
          className="admin-aim-error-banner"
          style={{ background: toast.isError ? 'var(--error, #c0392b)' : 'var(--success, #0d7c99)' }}
        >
          {toast.msg}
        </div>
      )}
      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Created</th>
              <th>Category</th>
              <th>Area</th>
              <th>Sev</th>
              <th>Owner</th>
              <th>Scope</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={8} className="admin-aim-empty">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="admin-aim-empty">No feedback matches these filters.</td></tr>
            ) : items.map((it) => (
              <>
                <tr key={it.id} onClick={() => toggleExpand(it.id, it)} style={{ cursor: 'pointer' }}>
                  <td className="admin-aim-rel">{fmtDate(it.createdAt)}</td>
                  <td>
                    {it.polarity === 'positive' && (
                      <span className="admin-badge admin-badge--basic" style={{ marginRight: 6 }}>
                        👍 {POLARITY_LABELS.positive}
                      </span>
                    )}
                    {it.evalCandidate && (
                      <span className="admin-badge admin-badge--warning" style={{ marginRight: 6 }} title="Candidate for the AI eval suite">
                        ⭐ Eval template
                      </span>
                    )}
                    {CATEGORY_LABELS[it.category] || it.category}{it.hasScreenshot ? ' 📷' : ''}
                  </td>
                  <td>{AREA_LABELS[it.area] || it.area}</td>
                  <td><span className={`admin-badge ${severityClass(it.severity)}`}>{SEVERITY_LABELS[it.severity] || it.severity}</span></td>
                  <td>{it.assignedToEmail || '—'}</td>
                  <td>{it.scope === 'message' ? `turn #${it.targetTurnIndex}` : 'conversation'}</td>
                  <td>{it.status}</td>
                  <td>{expanded.has(it.id) ? '▾' : '▸'}</td>
                </tr>
                {expanded.has(it.id) && (
                  <tr key={`${it.id}-detail`} className="admin-aim-detail-row">
                    <td colSpan={8}>
                      <div className="admin-aim-detail">
                        <p style={{ whiteSpace: 'pre-wrap', margin: '0 0 8px' }}>{it.comment}</p>
                        {it.hasScreenshot && shotUrls[it.id] && (
                          <a href={shotUrls[it.id]} target="_blank" rel="noreferrer">
                            <img
                              src={shotUrls[it.id]}
                              alt="feedback screenshot"
                              style={{ maxWidth: '320px', maxHeight: '220px', borderRadius: 6, border: '1px solid var(--glass-border, #333)', margin: '0 0 8px' }}
                            />
                          </a>
                        )}
                        <div className="admin-aim-detail-meta">
                          <span>Reporter: {it.reportedBy || '—'}</span>
                          {' · '}
                          <span>Model: {it.modelLabel || it.modelId || '—'}</span>
                          {' · '}
                          <span>Conversation: {it.conversationTitle || it.conversationId}</span>
                        </div>
                        <div className="admin-aim-detail-actions" style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <button type="button" className="admin-ai-btn admin-ai-btn--send"
                            disabled={busyId === it.id}
                            onClick={() => exportBundle(it, 'copy')}>
                            Copy for Claude Code
                          </button>
                          <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
                            disabled={busyId === it.id}
                            onClick={() => exportBundle(it, 'download')}>
                            {it.hasScreenshot ? 'Download .md + image' : 'Download .md'}
                          </button>
                          {!(myEmail && it.assignedToEmail === myEmail) && (
                            <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
                              disabled={busyId === it.id}
                              onClick={() => patch(it.id, { assignToMe: true })}>
                              Assign to me
                            </button>
                          )}
                          {it.assignedToUserId != null && (
                            <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
                              disabled={busyId === it.id}
                              onClick={() => patch(it.id, { assignedToCleared: true })}>
                              Unassign
                            </button>
                          )}
                          {it.status === 'open' ? (
                            <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
                              disabled={busyId === it.id}
                              onClick={() => patch(it.id, { status: 'resolved' })}>
                              Mark resolved
                            </button>
                          ) : (
                            <button type="button" className="admin-ai-btn admin-ai-btn--ghost"
                              disabled={busyId === it.id}
                              onClick={() => patch(it.id, { status: 'open' })}>
                              Reopen
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
